import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/providers/AuthProvider'
import { I18nProvider, ThemeProvider, ToastProvider } from '@/providers/UiProviders'
import { AppShell } from '@/shell/AppShell'
import { LockScreen, LoginScreen, SessionExpiredScreen } from '@/screens/auth/AuthScreens'
import { DashboardScreen } from '@/screens/DashboardScreen'
import { MyTasks } from '@/screens/MyTasks'
import { RealtimeMonitor } from '@/screens/RealtimeMonitor'
// A12/A13/A25/A26/A27 modul-ekranlari — registratsiya (supervisor 2026-07-23; backendlar 401=live)
import { RobotsEditorScreen } from '@/screens/seo/RobotsEditorScreen'
import { RedirectsScreen } from '@/screens/seo/RedirectsScreen'
import { NotFoundMonitorScreen } from '@/screens/seo/NotFoundMonitorScreen'
import { AppsScreen } from '@/screens/developers/AppsScreen'
import { BotTokensScreen } from '@/screens/developers/BotTokensScreen'
import { LeakedTokensScreen } from '@/screens/developers/LeakedTokensScreen'
import { WebhooksScreen } from '@/screens/developers/WebhooksScreen'
import { OAuthAppsScreen } from '@/screens/developers/OAuthAppsScreen'
import { RateLimitsScreen } from '@/screens/developers/RateLimitsScreen'
import { SuspiciousQueueScreen } from '@/screens/antifraud/SuspiciousQueueScreen'
import { RulesScreen } from '@/screens/antifraud/RulesScreen'
import { BlacklistsScreen } from '@/screens/antifraud/BlacklistsScreen'
import { DevicesScreen } from '@/screens/antifraud/DevicesScreen'
import { PaymentFraudScreen } from '@/screens/antifraud/PaymentFraudScreen'
import { ReferralAbuseScreen } from '@/screens/antifraud/ReferralAbuseScreen'
import { WorkflowsScreen } from '@/screens/automation/WorkflowsScreen'
import { ExecutionsScreen } from '@/screens/automation/ExecutionsScreen'
import { ScheduledTasksScreen } from '@/screens/automation/ScheduledTasksScreen'
import { TriggersScreen } from '@/screens/automation/TriggersScreen'
import { OutgoingWebhooksScreen } from '@/screens/automation/OutgoingWebhooksScreen'
import { ErrorsScreen } from '@/screens/automation/ErrorsScreen'
import { ApiConsoleScreen } from '@/screens/devtools/ApiConsoleScreen'
import { CacheScreen } from '@/screens/devtools/CacheScreen'
import { HealthScreen } from '@/screens/devtools/HealthScreen'
import { TestUsersScreen } from '@/screens/devtools/TestUsersScreen'
import { MigrationsScreen } from '@/screens/devtools/MigrationsScreen'
import { RestartScreen } from '@/screens/devtools/RestartScreen'
import { GrowthDashboard } from '@/screens/GrowthDashboard'
import { ModerationQueue } from '@/screens/moderation/ModerationQueue'
import { ModerationDashboard } from '@/screens/moderation/ModerationDashboard'
import { CategoryQueue } from '@/screens/moderation/CategoryQueue'
import { ReportGroups } from '@/screens/moderation/ReportGroups'
import { BulkDecision } from '@/screens/moderation/BulkDecision'
import { PriorityQueue } from '@/screens/moderation/PriorityQueue'
import { MyQueue } from '@/screens/moderation/MyQueue'
import { EscalatedQueue } from '@/screens/moderation/EscalatedQueue'
import { ReporterReliability } from '@/screens/moderation/ReporterReliability'
import { ReportCard } from '@/screens/moderation/ReportCard'
import { TicketQueue } from '@/screens/support/TicketQueue'
import { SupportDashboard } from '@/screens/support/SupportDashboard'
import { TicketCard } from '@/screens/support/TicketCard'
import { TicketsSla } from '@/screens/support/TicketsSla'
import { TicketsOverdue } from '@/screens/support/TicketsOverdue'
import { TvScreen } from '@/screens/TvScreen'
import { ModulesOverview, ModuleIndex, PlannedScreen } from '@/screens/modules/ModulesScreen'
import { ModuleTogglesScreen } from '@/screens/platform/ModuleTogglesScreen'
import { DbBrowserScreen } from '@/screens/platform/DbBrowserScreen'
import {
  InfraServicesScreen,
  InfraRedisScreen,
  InfraStorageScreen,
  InfraQueueScreen,
} from '@/screens/infra/InfraDetailScreens'
import { IncidentsScreen } from '@/screens/incidents/IncidentsScreen'
import { OnCallScreen } from '@/screens/incidents/OnCallScreen'
import { AutoModRulesScreen } from '@/screens/automod/AutoModRulesScreen'
import { ContentFlagsScreen } from '@/screens/automod/ContentFlagsScreen'
import { LegalDocsScreen } from '@/screens/legaldocs/LegalDocsScreen'
import { StatusPageScreen } from '@/screens/statuspage/StatusPageScreen'
import { JITAccessScreen, BreakGlassScreen } from '@/screens/jit/JITScreens'
import { MaintenanceScreen } from '@/screens/maintenance/MaintenanceScreen'
import { AnnouncementsScreen } from '@/screens/announcements/AnnouncementsScreen'
import { GDPRRequestsScreen } from '@/screens/gdpr/GDPRRequestsScreen'
import { LegalHoldsScreen } from '@/screens/legalholds/LegalHoldsScreen'
import { EmergencyScreen } from '@/screens/emergency/EmergencyScreen'
import { JobRunsScreen } from '@/screens/jobs/JobRunsScreen'
import { TakedownScreen } from '@/screens/takedowns/TakedownScreen'
import { CannedResponsesScreen } from '@/screens/canned/CannedResponsesScreen'
import { ComplianceDashboard } from '@/screens/compliance/ComplianceDashboard'
import { EscalationScreen } from '@/screens/escalation/EscalationScreen'
import { DataProcessorsScreen } from '@/screens/processors/DataProcessorsScreen'
import { ReliabilityDashboard } from '@/screens/reliability/ReliabilityDashboard'
import { SecurityPostureDashboard } from '@/screens/securityposture/SecurityPostureDashboard'
import { OpsHubScreen } from '@/screens/opshub/OpsHubScreen'
import { AdvertisersScreen } from '@/screens/ads/AdvertisersScreen'
import { CampaignsScreen } from '@/screens/ads/CampaignsScreen'
import { CreativesModerationScreen } from '@/screens/ads/CreativesModerationScreen'
import { CampaignApprovalScreen } from '@/screens/ads/CampaignApprovalScreen'
import {
  AdsBannedCategoriesScreen,
  AdsStatsScreen,
  AdsChannelRevenueScreen,
  AdsComplaintsScreen,
  AdsPolicyScreen,
} from '@/screens/ads/AdsExtraScreens'
import { UsersScreen } from '@/screens/users/UsersScreen'
import { BanAuditLog } from '@/screens/users/BanAuditLog'
import { BannedUsers } from '@/screens/users/BannedUsers'
import { UserStrikes } from '@/screens/users/UserStrikes'
import { UserTickets } from '@/screens/users/UserTickets'
import { UserReports } from '@/screens/users/UserReports'
import { UserBots } from '@/screens/users/UserBots'
import { StrikesQueue } from '@/screens/moderation/StrikesQueue'
import { AppealsQueue } from '@/screens/moderation/AppealsQueue'
import { AppealCard } from '@/screens/moderation/AppealCard'
import { VerificationQueue } from '@/screens/verification/VerificationQueue'
import { VerificationCard } from '@/screens/verification/VerificationCard'
import { VerifiedRegistry } from '@/screens/verification/VerifiedRegistry'
import { VerificationRejected } from '@/screens/verification/VerificationRejected'
import { VerificationStats } from '@/screens/verification/VerificationStats'
import { LegalQueue } from '@/screens/legal/LegalQueue'
import { LegalCard } from '@/screens/legal/LegalCard'
import { LegalRejected } from '@/screens/legal/LegalRejected'
import { LegalTransparency } from '@/screens/legal/LegalTransparency'
import { StrikeStats } from '@/screens/users/StrikeStats'
import { UserCard } from '@/screens/users/UserCard'
import { UserNotes } from '@/screens/users/UserNotes'
import { UserRisk } from '@/screens/users/UserRisk'
import { UserLoginHistory } from '@/screens/users/UserLoginHistory'
import { UserWarnings } from '@/screens/users/UserWarnings'
import { UserSessions } from '@/screens/users/UserSessions'
import { UserGroups } from '@/screens/users/UserGroups'
import { UserWallet } from '@/screens/users/UserWallet'
import { UserGifts } from '@/screens/users/UserGifts'
import { UserRelated } from '@/screens/users/UserRelated'
import { UserOwnedGroups } from '@/screens/users/UserOwnedGroups'
import { UserContact } from '@/screens/users/UserContact'
import { TransactionsScreen } from '@/screens/finance/TransactionsScreen'
import { WalletsScreen } from '@/screens/finance/WalletsScreen'
import { SellerApplications } from '@/screens/market/SellerApplications'
import { Categories as MarketCategories } from '@/screens/market/Categories'
import { EarnRules } from '@/screens/market/EarnRules'
import { PointsCatalog } from '@/screens/market/PointsCatalog'
import { Suspicions } from '@/screens/market/Suspicions'
import { IptvChannels } from '@/screens/media/IptvChannels'
import { MusicCatalog } from '@/screens/media/MusicCatalog'
import { MusicModeration } from '@/screens/media/MusicModeration'
import { BlockedCountries } from '@/screens/media/BlockedCountries'
import { VipNumbers } from '@/screens/media/VipNumbers'
import { StickerModeration } from '@/screens/stickers/StickerModeration'
import { BotsScreen } from '@/screens/bots/BotsScreen'
import { BotCard } from '@/screens/bots/BotCard'
import {
  BotOwner,
  BotWebhook,
  BotApps,
  BotBusiness,
  BotAffiliate,
  BotGames,
  BotStats,
  BotAppSettings,
  BotPreviewMedia,
  BotToken,
} from '@/screens/bots/BotSections'
import { TransactionCard } from '@/screens/finance/TransactionCard'
import { GroupsScreen } from '@/screens/groups/GroupsScreen'
import { GroupCard } from '@/screens/groups/GroupCard'
import { GroupMembers } from '@/screens/groups/GroupMembers'
import { GroupAdmins } from '@/screens/groups/GroupAdmins'
import { GroupBots } from '@/screens/groups/GroupBots'
import { GroupSettings } from '@/screens/groups/GroupSettings'
import { GroupReports } from '@/screens/groups/GroupReports'
import { GroupInvites } from '@/screens/groups/GroupInvites'
import { GroupJoinRequests } from '@/screens/groups/GroupJoinRequests'
import { GroupTopics } from '@/screens/groups/GroupTopics'
import { GroupMessages } from '@/screens/groups/GroupMessages'
import { GroupAdminLog } from '@/screens/groups/GroupAdminLog'
import { GroupMedia } from '@/screens/groups/GroupMedia'
import { GroupStats } from '@/screens/groups/GroupStats'
import { GroupBoosts } from '@/screens/groups/GroupBoosts'
import { GroupGrowth } from '@/screens/groups/GroupGrowth'
import {
  PlatformBreakdown,
  ChatTypeBreakdown,
  ActivityHeatmap,
  VersionBreakdown,
  TopBots,
  ByLanguage,
} from '@/screens/analytics/AnalyticsScreens'
import { PointsEconomy } from '@/screens/analytics/PointsEconomy'
import { ModerationKpi } from '@/screens/analytics/ModerationKpi'
import { ChannelBoosts } from '@/screens/analytics/ChannelBoosts'
import {
  EventDiscovery,
  EventTaxonomy,
  UserPath,
  FunnelComparison,
  SegmentComparison,
  CohortBuilder,
  Forecasting,
  ServiceQuality,
  ReportBuilder,
  AlertsCenter,
  DashboardBuilder,
} from '@/screens/analytics/AnalyticsSkeleton'
import {
  ActiveUsers,
  NewUsers,
  MessageVolume,
  Funnel,
  BotEcosystemStats,
  SessionDuration,
  Retention,
  Churn,
  ChannelGrowth,
  SupportKpi,
  AdRevenue,
  InviteLinks,
  RevenueDashboard,
  MetricsDictionary,
  ArpuArppu,
  MarketOverview,
  LiveOverview,
  StoriesOverview,
  WalletEconomy,
  PollsOverview,
  ReactionsOverview,
  MediaOverview,
  ContactsOverview,
} from '@/screens/analytics/AnalyticsMetrics'
import { BroadcastScreen } from '@/screens/broadcast/BroadcastScreen'
import { BroadcastCard } from '@/screens/broadcast/BroadcastCard'
import { ScheduledBroadcasts } from '@/screens/broadcast/ScheduledBroadcasts'
import { PushHealth } from '@/screens/broadcast/PushHealth'
import { TemplateLibrary } from '@/screens/broadcast/TemplateLibrary'
import { BroadcastSettings } from '@/screens/broadcast/BroadcastSettings'
import { ServiceBanner } from '@/screens/broadcast/ServiceBanner'
import { FeatureFlags } from '@/screens/settings/FeatureFlags'
import { FeatureFlagCard } from '@/screens/settings/FeatureFlagCard'
import { FeatureFlagChangeLog } from '@/screens/settings/FeatureFlagChangeLog'
import { RemoteConfigValues } from '@/screens/settings/RemoteConfigValues'
import { FeatureFlagToggle } from '@/screens/settings/FeatureFlagToggle'
import { FeatureRollout } from '@/screens/settings/FeatureRollout'
import { FeatureKillSwitch } from '@/screens/settings/FeatureKillSwitch'
import { FlagHistory } from '@/screens/settings/FlagHistory'
import { FlagSegments } from '@/screens/settings/FlagSegments'
import { FlagDependencies } from '@/screens/settings/FlagDependencies'
import { FlagSchema } from '@/screens/settings/FlagSchema'
import { Experiments } from '@/screens/settings/Experiments'
import { SystemSettings } from '@/screens/settings/SystemSettings'
import { ConfigHistory } from '@/screens/settings/ConfigHistory'
import { SmsProviders } from '@/screens/settings/SmsProviders'
import { MusicLibrary } from '@/screens/music/MusicLibrary'
import { FinanceReal } from '@/screens/finance/FinanceReal'
import { CommunitiesScreen } from '@/screens/community/CommunitiesScreen'
import { MyProfile, InterfaceSettings, Shortcuts, Help } from '@/screens/account/AccountScreens'
import { MySecurity, NotificationSettings } from '@/screens/account/AccountSecurity'
import { Localization } from '@/screens/localization/Localization'
import { LocalizationKeys } from '@/screens/localization/LocalizationKeys'
import { LocalizationMissing } from '@/screens/localization/LocalizationMissing'
import { LocalizationStats } from '@/screens/localization/LocalizationStats'
import { LocalizationPacks } from '@/screens/localization/LocalizationPacks'
import { LocalizationPlurals } from '@/screens/localization/LocalizationPlurals'
import { LocalizationQc } from '@/screens/localization/LocalizationQc'
import { LocalizationExport } from '@/screens/localization/LocalizationExport'
import { LocalizationGlossary } from '@/screens/localization/LocalizationGlossary'
import { LocalizationEditor } from '@/screens/localization/LocalizationEditor'
import { CountriesScreen } from '@/screens/geo/CountriesScreen'
import { CountryChangeLog } from '@/screens/geo/CountryChangeLog'
import { CountryCard } from '@/screens/geo/CountryCard'
import { CountryGrowth } from '@/screens/geo/CountryGrowth'
import { DialPrefixes } from '@/screens/geo/DialPrefixes'
import { GeoSanctions } from '@/screens/geo/GeoSanctions'
import { FinanceDashboard } from '@/screens/FinanceDashboard'
import { InfraDashboard } from '@/screens/InfraDashboard'
import { SecurityScreen } from '@/screens/security/SecurityScreen'
import { SecurityDashboard } from '@/screens/security/SecurityDashboard'
import { StaffScreen } from '@/screens/staff/StaffScreen'
import { DangerDemoScreen } from '@/screens/DangerDemoScreen'
import { AuthDemoScreen } from '@/screens/auth/AuthDemoScreen'
// --- BATCH-1 (2026-07-26): A1/A9/A14/A19/A22/A26 missing screens ---
import { HardwareTwoFAScreen } from '@/screens/auth/HardwareTwoFAScreen'
import { OnboardingScreen } from '@/screens/auth/OnboardingScreen'
import { LocalizationVersions } from '@/screens/localization/LocalizationVersions'
import { LocalizationCommunity } from '@/screens/localization/LocalizationCommunity'
import { LocalizationTranslators } from '@/screens/localization/LocalizationTranslators'
import { LocalizationScreenshots } from '@/screens/localization/LocalizationScreenshots'
import { AbTestResults } from '@/screens/settings/AbTestResults'
import { RolloutStatus } from '@/screens/settings/RolloutStatus'
import { LegalBasisScreen } from '@/screens/legal/LegalBasisScreen'
import { LegalJurisdictionScreen } from '@/screens/legal/LegalJurisdictionScreen'
import { LegalBlockingScreen } from '@/screens/legal/LegalBlockingScreen'
import { LegalDataCollectionScreen } from '@/screens/legal/LegalDataCollectionScreen'
import { LegalResponsePrepScreen } from '@/screens/legal/LegalResponsePrepScreen'
import { LegalSendConfirmScreen } from '@/screens/legal/LegalSendConfirmScreen'
import { LegalDataExportScreen } from '@/screens/legal/LegalDataExportScreen'
import { LegalErasureScreen } from '@/screens/legal/LegalErasureScreen'
import { VerificationReview } from '@/screens/verification/VerificationReview'
import { VerificationManage } from '@/screens/verification/VerificationManage'
import { ThirdPartyVerifiers } from '@/screens/verification/ThirdPartyVerifiers'
import { VerifierAudit } from '@/screens/verification/VerifierAudit'
import { WorkflowBuilderScreen } from '@/screens/automation/WorkflowBuilderScreen'
import { ConditionsActionsScreen } from '@/screens/automation/ConditionsActionsScreen'
import { TestModeScreen } from '@/screens/automation/TestModeScreen'
import { IntegrationsScreen } from '@/screens/automation/IntegrationsScreen'
import { ScriptsScreen } from '@/screens/automation/ScriptsScreen'
import { AutomationAuditScreen } from '@/screens/automation/AutomationAuditScreen'
// --- BATCH-2 (2026-07-26): A4/A5/A15/A20/A23/A25 missing screens ---
import { AudienceSelect, SegmentBuilder } from '@/screens/broadcast/AudienceScreen'
import { MessageEditor, AbVariants } from '@/screens/broadcast/MessageEditorScreen'
import { ApprovalFlow } from '@/screens/broadcast/ApprovalFlow'
import { DeliveryStats, OpenClickStats, Unsubscribes } from '@/screens/broadcast/DeliveryAnalytics'
import { PushTokenHealth } from '@/screens/broadcast/PushTokenHealth'
import { RiskDashboardScreen } from '@/screens/antifraud/RiskDashboardScreen'
import { RuleCreateScreen } from '@/screens/antifraud/RuleCreateScreen'
import { BacktestScreen } from '@/screens/antifraud/BacktestScreen'
import { MlModelsScreen } from '@/screens/antifraud/MlModelsScreen'
import { ScoringConfigScreen } from '@/screens/antifraud/ScoringConfigScreen'
import { AutoActionsScreen } from '@/screens/antifraud/AutoActionsScreen'
import { LinkedAccountsScreen } from '@/screens/antifraud/LinkedAccountsScreen'
import { SyntheticActivityScreen } from '@/screens/antifraud/SyntheticActivityScreen'
import { StarsFraudScreen } from '@/screens/antifraud/StarsFraudScreen'
import { FraudReportsScreen } from '@/screens/antifraud/FraudReportsScreen'
import { RateLimitSettingsScreen } from '@/screens/bots/RateLimitSettingsScreen'
import { RestrictBotScreen } from '@/screens/bots/RestrictBotScreen'
import { DeleteBotScreen } from '@/screens/bots/DeleteBotScreen'
import { VipLimitScreen } from '@/screens/bots/VipLimitScreen'
import { AppModerationQueue } from '@/screens/market/AppModerationQueue'
import { AppUrlSecurity } from '@/screens/market/AppUrlSecurity'
import { AppCatalogScreen } from '@/screens/market/AppCatalogScreen'
import { AppCategoriesScreen } from '@/screens/market/AppCategoriesScreen'
import { AppReviewsScreen } from '@/screens/market/AppReviewsScreen'
import { AppSuspendScreen } from '@/screens/market/AppSuspendScreen'
import { AppDeleteScreen } from '@/screens/market/AppDeleteScreen'
import { AdvertiserCardScreen, AdvertiserBalanceScreen, TargetingScreen } from '@/screens/ads/AdsDetailScreens'
import { PlacementsScreen, PlacementRemoveScreen } from '@/screens/ads/AdsPlacementScreens'
import { AdPlacementsOverviewScreen, PricingCpmScreen, AuctionSettingsScreen, FraudAdsScreen, BrandSafetyScreen, AdsReportsScreen } from '@/screens/ads/AdsMoreScreens'
import { GroupAdvancedSearch } from '@/screens/groups/GroupAdvancedSearch'
import { GroupBulkActions } from '@/screens/groups/GroupBulkActions'
import { GroupLinkedChats } from '@/screens/groups/GroupLinkedChats'
import { GroupPinnedMessages } from '@/screens/groups/GroupPinnedMessages'
import { GroupAdPosts } from '@/screens/groups/GroupAdPosts'
import { GroupMonetization } from '@/screens/groups/GroupMonetization'
import { GroupSendWarning } from '@/screens/groups/GroupSendWarning'
import { GroupRestrict } from '@/screens/groups/GroupRestrict'
import { GroupPurgePosts } from '@/screens/groups/GroupPurgePosts'
import { GroupHideFromSearch } from '@/screens/groups/GroupHideFromSearch'
import { GroupArchive } from '@/screens/groups/GroupArchive'
import { GroupDelete } from '@/screens/groups/GroupDelete'
import { RoleEditorScreen } from '@/screens/staff/RoleEditorScreen'
import { AccessRequestsScreen } from '@/screens/staff/AccessRequestsScreen'
import { StaffJITScreen } from '@/screens/jit/StaffJITScreen'
import { AssignmentRulesScreen, ShiftsScreen, WorkloadScreen, PerformanceKpiScreen, OnboardingScreen as StaffOnboardingScreen, OffboardingScreen, StaffActivityAuditScreen, StaffAnnouncementsScreen, TrainingScreen, StaffSettingsScreen } from '@/screens/staff/StaffOrgScreens'
// --- BATCH-3 (2026-07-26): A8/A11/A13/A16/A17/A18 missing screens ---
import { AutoRulesScreen } from '@/screens/strikes/AutoRulesScreen'
import { ManualRestrictionScreen } from '@/screens/strikes/ManualRestrictionScreen'
import { RevokeRestrictionScreen } from '@/screens/strikes/RevokeRestrictionScreen'
import { ChangeDurationScreen } from '@/screens/strikes/ChangeDurationScreen'
import { SpamWaveScreen } from '@/screens/strikes/SpamWaveScreen'
import { RestrictionTemplatesScreen } from '@/screens/strikes/RestrictionTemplatesScreen'
import { AppealDecisionsScreen } from '@/screens/strikes/AppealDecisionsScreen'
import { MassAmnestyScreen } from '@/screens/strikes/MassAmnestyScreen'
import { WrongfulBanAnalysisScreen } from '@/screens/strikes/WrongfulBanAnalysisScreen'
import { BanReasonsCatalogScreen } from '@/screens/strikes/BanReasonsCatalogScreen'
import { DeviceBlocksScreen } from '@/screens/strikes/DeviceBlocksScreen'
import { PhoneBlocksScreen } from '@/screens/strikes/PhoneBlocksScreen'
import { ReregistrationPreventionScreen } from '@/screens/strikes/ReregistrationPreventionScreen'
import { FairnessReportScreen } from '@/screens/strikes/FairnessReportScreen'
import { AppCardScreen, UsageStatsScreen } from '@/screens/developers/AppInsightScreens'
import { AppBlockScreen, ForcedRevokeScreen } from '@/screens/developers/AppControlScreens'
import { KmsKeysScreen, KeyExportScreen, CertsScreen } from '@/screens/developers/KeyInfraScreens'
import { AbuseDetectionScreen, VipLimitsScreen, RotationLogScreen, ServiceKeysScreen, ProviderKeysScreen, KeyPolicyScreen, KeyAuditScreen } from '@/screens/developers/KeyGovernanceScreens'
import { IncidentsScreen as SecurityIncidentsScreen } from '@/screens/security/IncidentsScreen'
import { FraudRulesScreen } from '@/screens/security/FraudRulesScreen'
import { FraudCasesScreen } from '@/screens/security/FraudCasesScreen'
import { AnomalyScreen } from '@/screens/security/AnomalyScreen'
import { BotFarmsScreen } from '@/screens/security/BotFarmsScreen'
import { MassRegistrationScreen } from '@/screens/security/MassRegistrationScreen'
import { CredentialStuffingScreen } from '@/screens/security/CredentialStuffingScreen'
import { DdosScreen } from '@/screens/security/DdosScreen'
import { WafRulesScreen } from '@/screens/security/WafRulesScreen'
import { IpListsScreen } from '@/screens/security/IpListsScreen'
import { VulnScansScreen } from '@/screens/security/VulnScansScreen'
import { DependencyAuditScreen } from '@/screens/security/DependencyAuditScreen'
import { PentestScreen } from '@/screens/security/PentestScreen'
import { SecurityPoliciesScreen } from '@/screens/security/SecurityPoliciesScreen'
import { CountriesMapScreen } from '@/screens/geo/CountriesMapScreen'
import { StagedRolloutScreen } from '@/screens/geo/StagedRolloutScreen'
import { FeatureRestrictionsScreen } from '@/screens/geo/FeatureRestrictionsScreen'
import { PaymentRestrictionsScreen } from '@/screens/geo/PaymentRestrictionsScreen'
import { ContentRestrictionsScreen } from '@/screens/geo/ContentRestrictionsScreen'
import { LocaleSettingsScreen } from '@/screens/geo/LocaleSettingsScreen'
import { PhoneOperatorsScreen } from '@/screens/geo/PhoneOperatorsScreen'
import { BlockedRangesScreen } from '@/screens/geo/BlockedRangesScreen'
import { IpGeoBaseScreen } from '@/screens/geo/IpGeoBaseScreen'
import { VpnProxyScreen } from '@/screens/geo/VpnProxyScreen'
import { TorNodesScreen } from '@/screens/geo/TorNodesScreen'
import { DcRoutingScreen } from '@/screens/geo/DcRoutingScreen'
import { DataResidencyScreen } from '@/screens/geo/DataResidencyScreen'
import { LegalRegistryScreen } from '@/screens/geo/LegalRegistryScreen'
import { GeoAnomaliesScreen } from '@/screens/geo/GeoAnomaliesScreen'
import { PremiumPricingScreen } from '@/screens/geo/PremiumPricingScreen'
import { ScreenTime, FunnelBuilder, Ltv, ConversionFunnel, ViralCoefficient, PremiumConversion } from '@/screens/analytics/AnalyticsFunnelSkeletons'
import { BotEcosystemOverview } from '@/screens/analytics/BotEcosystem'
import { ScheduledReports } from '@/screens/analytics/ScheduledReports'
import { ExportCenter } from '@/screens/analytics/ExportCenter'
import { DataDictionary } from '@/screens/analytics/DataDictionary'
import { InfraErrorLogsScreen, InfraDbScreen, InfraMigrationsScreen } from '@/screens/infra/InfraMonitorScreens'
import { InfraPlanned } from '@/screens/infra/InfraPlanned'
// --- A17.02-22 single-node infra (real host/DC/servers/slow-q/api-metrics/cdn/backups/deploys) ---
import {
  InfraServersScreen,
  InfraDataCentersScreen,
  InfraDcLoadScreen,
  InfraDcAssignmentScreen,
} from '@/screens/infra/InfraServerScreens'
import {
  InfraMigrationControlScreen,
  InfraQueuesScreen,
  InfraSlowQueriesScreen,
  InfraApiLatencyScreen,
} from '@/screens/infra/InfraOpsScreens'
import {
  InfraCdnScreen,
  InfraBackupsScreen,
  InfraRestoreDrillScreen,
  InfraDeployHistoryScreen,
  InfraRollbackScreen,
} from '@/screens/infra/InfraPipelineScreens'
// --- BATCH-4 (2026-07-26): A3/A6/A7/A12/A21/A24 missing screens ---
import { BrandingSettings, DomainsSettings, SmtpSettings, FileStorageSettings, SearchIndexSettings } from '@/screens/settings/PlatformConfigScreens'
import { RegistrationSettings, MediaLimits, MessageLimits, GroupLimits, BotLimits } from '@/screens/settings/LimitsScreens'
import { PremiumPackages, Pricing } from '@/screens/settings/CommerceScreens'
import { CacheManagement } from '@/screens/settings/CacheScreen'
import { PushCertificates, CdnSettings } from '@/screens/settings/InfraSkeletonScreens'
import { ComplaintsList, ComplaintCard, UnfoundedComplaints } from '@/screens/tickets/ComplaintsScreens'
import { CorrespondenceHistory, AssignmentQueue, PriorityBoard } from '@/screens/tickets/TicketTriageScreens'
import { TagsCategories, MergeDuplicates, MacrosScreen } from '@/screens/tickets/TicketTaxonomyScreens'
import { InternalDiscussion, KnowledgeBase, AutoReplies } from '@/screens/tickets/TicketCollabScreens'
import { CsatScreen, AgentStats } from '@/screens/tickets/TicketInsightScreens'
import { TeamWorkload, TicketReports } from '@/screens/tickets/TicketReportsScreens'
import { AdvancedUserSearch, SavedSegments, UserImport } from '@/screens/users/UserSearchScreens'
import { BulkUserActions, UserExport } from '@/screens/users/UserBulkScreens'
import { PiiAccessLog, UserDataExport, UserMessageView } from '@/screens/users/UserComplianceScreens'
import { ProfileHistory, UsernameChange, AvatarScrub } from '@/screens/users/UserProfileScreens'
import { UserRestrict, UserShadowban } from '@/screens/users/UserRestrictScreens'
import { AccountDelete, AccountRestore, Impersonation } from '@/screens/users/UserLifecycleScreens'
import { UserReward } from '@/screens/users/UserRewardScreen'
import { StickerModerationScreen, TrendingStickersScreen, CopyrightComplaintsScreen, StickerAuthorsScreen, TgsValidationScreen } from '@/screens/catalog/StickerCatalogScreens'
import { CustomEmojiSetsScreen, EmojiStatusesScreen, ReactionsManagementScreen, EmojiModerationScreen } from '@/screens/catalog/EmojiReactionScreens'
import { FeaturedCatalogScreen, MiniAppCatalogScreen, CatalogCategoriesScreen, RecommendationSettingsScreen, SearchRankingScreen, CatalogReportsScreen } from '@/screens/catalog/CatalogDirectoryScreens'
import { MediaStorageStatsScreen, DuplicateMediaScreen } from '@/screens/catalog/MediaStorageScreens'
import { SeoDashboardScreen } from '@/screens/seo/DashboardScreen'
import { SitemapScreen } from '@/screens/seo/SitemapScreen'
import { MetaTemplatesScreen, OpenGraphScreen } from '@/screens/seo/MetaScreens'
import { CanonicalScreen, NoindexScreen } from '@/screens/seo/IndexingScreens'
import { IndexPolicyScreen, SchemaOrgScreen } from '@/screens/seo/PolicySchemaScreens'
import { HreflangScreen } from '@/screens/seo/HreflangScreen'
import { LandingPagesScreen, BlogCmsScreen } from '@/screens/seo/ContentScreens'
import { MiniAppSeoScreen, DeepLinkPreviewScreen } from '@/screens/seo/MiniAppDeepLinkScreens'
import { KeywordsScreen, CompetitorsScreen } from '@/screens/seo/AnalyticsScreens'
import { ContentGapsScreen, InternalLinksScreen } from '@/screens/seo/ContentAnalysisScreens'
import { WebVitalsScreen, PageSpeedScreen, MobileFriendlyScreen } from '@/screens/seo/PerformanceScreens'
import { SearchConsoleScreen, YandexWebmasterScreen } from '@/screens/seo/SearchEngineScreens'
import { SeoAuditScreen } from '@/screens/seo/AuditScreen'
import { BotModerationScreen, StickerEmojiModerationScreen } from '@/screens/moderation/BotStickerModerationScreens'
import { UsernameModerationScreen, AvatarModerationScreen } from '@/screens/moderation/IdentityModerationScreens'
import { ContextViewScreen, UserHistoryPanelScreen } from '@/screens/moderation/CaseContextScreens'
import { DecisionFormScreen, SecondReviewQueueScreen, SecondOpinionScreen } from '@/screens/moderation/DecisionScreens'
import { HashDatabaseScreen, FileHashBlacklistScreen, WhitelistsScreen } from '@/screens/moderation/HashListScreens'
import { KeywordListsScreen, RuleBuilderScreen, RuleTestScreen } from '@/screens/moderation/KeywordRuleScreens'
import { DecisionAuditScreen, ErrorDecisionsScreen } from '@/screens/moderation/DecisionAuditScreens'
import { TrainingModeScreen, ModerationPolicyScreen, TransparencyReportScreen } from '@/screens/moderation/PolicyReportScreens'
import { SimilarCasesScreen, ModMlModelsScreen, ModelQualityScreen, ThresholdTuningScreen, PerceptualHashScreen, InterRaterScreen } from '@/screens/moderation/MlModerationScreens'
// --- BATCH-5 (2026-07-26): A27 dev-tools (final 8) ---
import { RequestInspectorScreen } from '@/screens/devtools/RequestInspectorScreen'
import { WebhookDebugScreen } from '@/screens/devtools/WebhookDebugScreen'
import { UpdateViewerScreen } from '@/screens/devtools/UpdateViewerScreen'
import { EventStreamScreen } from '@/screens/devtools/EventStreamScreen'
import { ErrorTraceScreen } from '@/screens/devtools/ErrorTraceScreen'
import { SandboxScreen } from '@/screens/devtools/SandboxScreen'
import { DataGeneratorScreen } from '@/screens/devtools/DataGeneratorScreen'
import { ConfigDiffScreen } from '@/screens/devtools/ConfigDiffScreen'
import { EmptyState } from '@/components/States'
import { useT } from '@/i18n'

function NotFound() {
  const t = useT()
  return <EmptyState title={t.states.notFoundTitle} />
}

/** A1 — sessiya holatiga qarab nima ko'rsatilishini hal qiladi. */
function AuthGate() {
  const { state, signOut } = useAuth()

  if (state === 'loading') return <div className="min-h-screen bg-surface-0" />
  if (state === 'anonymous') return <LoginScreen />
  if (state === 'expired') return <SessionExpiredScreen onContinue={signOut} />
  if (state === 'locked') return <LockScreen />

  return (
    <Routes>
      {/* A2.12 — Ekran rejimi: to'liq ekran, AppShell (sidebar/topbar) SIZ. */}
      <Route path="/tv" element={<TvScreen />} />
      <Route
        path="*"
        element={
          <AppShell>
            <Routes>
              <Route path="/" element={<DashboardScreen />} />
              <Route path="/realtime" element={<RealtimeMonitor />} />
              <Route path="/my-tasks" element={<MyTasks />} />
              <Route path="/infra" element={<InfraDashboard />} />
              <Route path="/infra/services" element={<InfraServicesScreen />} /> {/* A17.01 */}
              <Route path="/infra/redis" element={<InfraRedisScreen />} /> {/* A17.15 */}
              <Route path="/infra/storage" element={<InfraStorageScreen />} /> {/* A17.16 */}
              <Route path="/infra/queue" element={<InfraQueueScreen />} /> {/* A17.18 */}
              <Route path="/incidents" element={<IncidentsScreen />} /> {/* A17.25/26/27 */}
              <Route path="/oncall" element={<OnCallScreen />} /> {/* A17.28 */}
              <Route path="/automod/rules" element={<AutoModRulesScreen />} /> {/* A6.29/30/35 */}
              <Route path="/automod/flags" element={<ContentFlagsScreen />} /> {/* A6.36 */}
              <Route path="/legal-docs" element={<LegalDocsScreen />} /> {/* A24.19/20 */}
              <Route path="/status-page" element={<StatusPageScreen />} /> {/* A17.28 */}
              <Route path="/jit/access" element={<JITAccessScreen />} /> {/* A18.07 */}
              <Route path="/jit/break-glass" element={<BreakGlassScreen />} /> {/* A18.08 */}
              <Route path="/maintenance-windows" element={<MaintenanceScreen />} /> {/* A24.21 */}
              <Route path="/announcements" element={<AnnouncementsScreen />} /> {/* A15.19/20 */}
              <Route path="/gdpr-requests" element={<GDPRRequestsScreen />} /> {/* A19.14 */}
              <Route path="/legal-holds" element={<LegalHoldsScreen />} /> {/* A19.06 */}
              <Route path="/emergency-requests" element={<EmergencyScreen />} /> {/* A19.05 */}
              <Route path="/job-runs" element={<JobRunsScreen />} /> {/* A17.08/09 */}
              <Route path="/takedowns" element={<TakedownScreen />} /> {/* A19.07 */}
              <Route path="/canned-responses" element={<CannedResponsesScreen />} /> {/* A7.15 */}
              <Route path="/escalation-rules" element={<EscalationScreen />} /> {/* A7.17 */}
              <Route path="/compliance" element={<ComplianceDashboard />} /> {/* A18.23 */}
              <Route path="/data-processors" element={<DataProcessorsScreen />} /> {/* GDPR Art.30 — via compliance dashboard */}
              <Route path="/reliability" element={<ReliabilityDashboard />} /> {/* A17.13 */}
              <Route path="/security-posture" element={<SecurityPostureDashboard />} /> {/* A18.10 */}
              <Route path="/ops-hub" element={<OpsHubScreen />} /> {/* lane-A operations hub (nav) */}
              <Route path="/ads/advertisers" element={<AdvertisersScreen />} /> {/* A20.01 */}
              <Route path="/ads/campaigns" element={<CampaignsScreen />} /> {/* A20.04 */}
              <Route path="/ads/creatives" element={<CreativesModerationScreen />} /> {/* A20.05 */}
              <Route path="/ads/approval" element={<CampaignApprovalScreen />} /> {/* A20.10 */}
              <Route path="/ads/banned-categories" element={<AdsBannedCategoriesScreen />} /> {/* A20.06 */}
              <Route path="/ads/stats" element={<AdsStatsScreen />} /> {/* A20.11 */}
              <Route path="/ads/channel-revenue" element={<AdsChannelRevenueScreen />} /> {/* A20.15 */}
              <Route path="/ads/complaints" element={<AdsComplaintsScreen />} /> {/* A20.16 */}
              <Route path="/ads/policy" element={<AdsPolicyScreen />} /> {/* A20.20 */}
              <Route path="/growth" element={<GrowthDashboard />} />
              <Route path="/moderation/dashboard" element={<ModerationDashboard />} />
              <Route path="/moderation" element={<ModerationQueue />} />
              <Route path="/moderation/groups" element={<ReportGroups />} />
              <Route path="/moderation/bulk" element={<BulkDecision />} /> {/* A6.20 */}
              <Route path="/moderation/priority" element={<PriorityQueue />} /> {/* A6.02 */}
              <Route path="/moderation/mine" element={<MyQueue />} /> {/* A6.13 */}
              <Route path="/moderation/escalated" element={<EscalatedQueue />} /> {/* A6.21 */}
              <Route path="/moderation/reporters" element={<ReporterReliability />} />
              <Route path="/moderation/cat/:category" element={<CategoryQueue />} />
              <Route path="/moderation/:id" element={<ReportCard />} />
              <Route path="/support/dashboard" element={<SupportDashboard />} />
              <Route path="/support" element={<TicketQueue />} />
              <Route path="/support/sla" element={<TicketsSla />} />
              <Route path="/support/overdue" element={<TicketsOverdue />} />
              <Route path="/support/:id" element={<TicketCard />} />
              <Route path="/modules" element={<ModulesOverview />} />
              <Route path="/m/:key" element={<ModuleIndex />} />
              <Route path="/screen/:id" element={<PlannedScreen />} />
              <Route path="/platform/modules" element={<ModuleTogglesScreen />} />
              <Route path="/platform/db" element={<DbBrowserScreen />} />
              <Route path="/users" element={<UsersScreen />} />
              <Route path="/users/ban-log" element={<BanAuditLog />} />
              <Route path="/users/banned" element={<BannedUsers />} />
              <Route path="/users/:id/strikes" element={<UserStrikes />} />
              <Route path="/users/:id/notes" element={<UserNotes />} /> {/* A3.19/A3.38 */}
              <Route path="/users/:id/risk" element={<UserRisk />} /> {/* A3.22 */}
              <Route path="/users/:id/login-history" element={<UserLoginHistory />} /> {/* A3.11 */}
              <Route path="/users/:id/warnings" element={<UserWarnings />} /> {/* A3.26 */}
              <Route path="/users/:id/tickets" element={<UserTickets />} />
              <Route path="/users/:id/reports" element={<UserReports />} />
              <Route path="/users/:id/bots" element={<UserBots />} />
              <Route path="/strikes" element={<StrikesQueue />} />
              <Route path="/strikes/stats" element={<StrikeStats />} />
              <Route path="/appeals" element={<AppealsQueue />} />
              <Route path="/appeals/:id" element={<AppealCard />} />
              <Route path="/verification" element={<VerificationQueue />} />
              <Route path="/verification/registry" element={<VerifiedRegistry />} />
              <Route path="/verification/rejected" element={<VerificationRejected />} />
              <Route path="/verification/stats" element={<VerificationStats />} />
              <Route path="/verification/:id" element={<VerificationCard />} />
              <Route path="/broadcasts" element={<BroadcastScreen />} />
              <Route path="/broadcasts/scheduled" element={<ScheduledBroadcasts />} />
              <Route path="/broadcasts/templates" element={<TemplateLibrary />} />
              <Route path="/broadcasts/settings" element={<BroadcastSettings />} />
              <Route path="/broadcasts/banner" element={<ServiceBanner />} />
              <Route path="/broadcasts/:id" element={<BroadcastCard />} />
              <Route path="/push/health" element={<PushHealth />} />
              <Route path="/legal" element={<LegalQueue />} />
              <Route path="/legal/rejected" element={<LegalRejected />} />
              <Route path="/legal/transparency" element={<LegalTransparency />} />
              <Route path="/legal/:id" element={<LegalCard />} />
              <Route path="/users/:id/sessions" element={<UserSessions />} />
              <Route path="/users/:id/groups" element={<UserGroups />} />
              <Route path="/users/:id/wallet" element={<UserWallet />} />
              <Route path="/users/:id/gifts" element={<UserGifts />} />
              <Route path="/users/:id/related" element={<UserRelated />} />
              <Route path="/users/:id/owned-groups" element={<UserOwnedGroups />} />
              <Route path="/users/:id/contact" element={<UserContact />} />
              <Route path="/users/:id" element={<UserCard />} />
              <Route path="/finance" element={<TransactionsScreen />} />
              <Route path="/finance/dashboard" element={<FinanceDashboard />} />
              <Route path="/finance/wallets" element={<WalletsScreen />} />
              <Route path="/finance/:id" element={<TransactionCard />} />
              <Route path="/market/sellers" element={<SellerApplications />} />
              <Route path="/market/categories" element={<MarketCategories />} />
              <Route path="/media/tv" element={<IptvChannels />} />
              <Route path="/media/music" element={<MusicCatalog />} />
              <Route path="/media/music/moderation" element={<MusicModeration />} />
              <Route path="/media/tv/blocked" element={<BlockedCountries />} />
              <Route path="/media/numbers" element={<VipNumbers />} />
              <Route path="/points/earn-rules" element={<EarnRules />} />
              <Route path="/points/catalog" element={<PointsCatalog />} />
              <Route path="/points/suspicions" element={<Suspicions />} />
              <Route path="/stickers/moderation" element={<StickerModeration />} />
              <Route path="/bots" element={<BotsScreen />} />
              <Route path="/bots/:id" element={<BotCard />} />
              <Route path="/bots/:id/owner" element={<BotOwner />} />
              <Route path="/bots/:id/webhook" element={<BotWebhook />} />
              <Route path="/bots/:id/apps" element={<BotApps />} />
              <Route path="/bots/:id/app-settings" element={<BotAppSettings />} />
              <Route path="/bots/:id/preview-media" element={<BotPreviewMedia />} />
              <Route path="/bots/:id/business" element={<BotBusiness />} />
              <Route path="/bots/:id/affiliate" element={<BotAffiliate />} />
              <Route path="/bots/:id/games" element={<BotGames />} />
              <Route path="/bots/:id/stats" element={<BotStats />} />
              <Route path="/bots/:id/token" element={<BotToken />} />
              <Route path="/groups" element={<GroupsScreen />} />
              <Route path="/groups/:id/members" element={<GroupMembers />} />
              <Route path="/groups/:id/admins" element={<GroupAdmins />} />
              <Route path="/groups/:id/bots" element={<GroupBots />} />
              <Route path="/groups/:id/settings" element={<GroupSettings />} />
              <Route path="/groups/:id/reports" element={<GroupReports />} />
              <Route path="/groups/:id/invites" element={<GroupInvites />} />
              <Route path="/groups/:id/join-requests" element={<GroupJoinRequests />} />
              <Route path="/groups/:id/topics" element={<GroupTopics />} />
              <Route path="/groups/:id/messages" element={<GroupMessages />} />
              <Route path="/groups/:id/media" element={<GroupMedia />} />
              <Route path="/groups/:id/stats" element={<GroupStats />} />
              <Route path="/groups/:id/boosts" element={<GroupBoosts />} />
              <Route path="/groups/:id/growth" element={<GroupGrowth />} />
              <Route path="/analytics/platform" element={<PlatformBreakdown />} />
              <Route path="/analytics/chat-types" element={<ChatTypeBreakdown />} />
              <Route path="/analytics/versions" element={<VersionBreakdown />} />
              <Route path="/analytics/bots" element={<BotEcosystemStats />} />
              <Route path="/analytics/top-bots" element={<TopBots />} />
              <Route path="/analytics/sessions" element={<SessionDuration />} />
              <Route path="/analytics/retention" element={<Retention />} />
              <Route path="/analytics/churn" element={<Churn />} />
              <Route path="/analytics/channel-growth" element={<ChannelGrowth />} />
              <Route path="/analytics/support" element={<SupportKpi />} />
              <Route path="/analytics/ad-revenue" element={<AdRevenue />} />
              <Route path="/analytics/invites" element={<InviteLinks />} />
              <Route path="/analytics/revenue" element={<RevenueDashboard />} />
              <Route path="/analytics/metrics-dict" element={<MetricsDictionary />} />
              <Route path="/analytics/arpu" element={<ArpuArppu />} />
              <Route path="/analytics/market" element={<MarketOverview />} />
              <Route path="/analytics/live" element={<LiveOverview />} />
              <Route path="/analytics/stories" element={<StoriesOverview />} />
              <Route path="/analytics/wallet" element={<WalletEconomy />} />
              <Route path="/analytics/polls" element={<PollsOverview />} />
              <Route path="/analytics/reactions" element={<ReactionsOverview />} />
              <Route path="/analytics/media" element={<MediaOverview />} />
              <Route path="/analytics/contacts" element={<ContactsOverview />} />
              <Route path="/analytics/languages" element={<ByLanguage />} />
              <Route path="/analytics/points" element={<PointsEconomy />} />
              <Route path="/analytics/moderation" element={<ModerationKpi />} />
              <Route path="/analytics/channel-boosts" element={<ChannelBoosts />} />
              <Route path="/analytics/events/discovery" element={<EventDiscovery />} />
              <Route path="/analytics/events/taxonomy" element={<EventTaxonomy />} />
              <Route path="/analytics/events/user-path" element={<UserPath />} />
              <Route path="/analytics/funnels/compare" element={<FunnelComparison />} />
              <Route path="/analytics/segments/compare" element={<SegmentComparison />} />
              <Route path="/analytics/cohorts" element={<CohortBuilder />} />
              <Route path="/analytics/forecast" element={<Forecasting />} />
              <Route path="/analytics/service-quality" element={<ServiceQuality />} />
              <Route path="/analytics/report-builder" element={<ReportBuilder />} />
              <Route path="/alerts-center" element={<AlertsCenter />} />
              <Route path="/dashboard-builder" element={<DashboardBuilder />} />
              <Route path="/analytics/activity" element={<ActivityHeatmap />} />
              <Route path="/analytics/active-users" element={<ActiveUsers />} />
              <Route path="/analytics/new-users" element={<NewUsers />} />
              <Route path="/analytics/messages" element={<MessageVolume />} />
              <Route path="/analytics/funnel" element={<Funnel />} />
              <Route path="/flags" element={<FeatureFlags />} />
              <Route path="/settings/system" element={<SystemSettings />} /> {/* A24.01 */}
              <Route path="/settings/sms-providers" element={<SmsProviders />} /> {/* A24.05 */}
              <Route path="/settings/config-history" element={<ConfigHistory />} /> {/* A24.22 */}
              <Route path="/music/library" element={<MusicLibrary />} /> {/* A21.19 */}
              <Route path="/finance/planned/:id" element={<FinanceReal />} /> {/* A10.03–36 real */}
              <Route path="/communities" element={<CommunitiesScreen />} /> {/* A23.08 */}
              <Route path="/flags/changelog" element={<FeatureFlagChangeLog />} />
              <Route path="/flags/config" element={<RemoteConfigValues />} />
              <Route path="/flags/toggle" element={<FeatureFlagToggle />} />
              <Route path="/flags/rollout" element={<FeatureRollout />} />
              <Route path="/flags/kill" element={<FeatureKillSwitch />} />
              <Route path="/flags/history" element={<FlagHistory />} />
              <Route path="/flags/segments" element={<FlagSegments />} />
              <Route path="/flags/deps" element={<FlagDependencies />} />
              <Route path="/flags/schema" element={<FlagSchema />} />
              <Route path="/flags/experiments" element={<Experiments />} />
              <Route path="/flags/:key" element={<FeatureFlagCard />} />
              <Route path="/account" element={<MyProfile />} />
              <Route path="/account/interface" element={<InterfaceSettings />} />
              <Route path="/account/shortcuts" element={<Shortcuts />} />
              <Route path="/account/help" element={<Help />} />
              <Route path="/account/security" element={<MySecurity />} />
              <Route path="/account/notifications" element={<NotificationSettings />} />
              <Route path="/localization" element={<Localization />} />
              <Route path="/localization/keys" element={<LocalizationKeys />} />
              <Route path="/localization/missing" element={<LocalizationMissing />} />
              <Route path="/localization/stats" element={<LocalizationStats />} />
              <Route path="/localization/packs" element={<LocalizationPacks />} />
              <Route path="/localization/plurals" element={<LocalizationPlurals />} />
              <Route path="/localization/qc" element={<LocalizationQc />} />
              <Route path="/localization/export" element={<LocalizationExport />} />
              <Route path="/localization/glossary" element={<LocalizationGlossary />} />
              <Route path="/localization/editor" element={<LocalizationEditor />} />
              <Route path="/groups/:id/admin-log" element={<GroupAdminLog />} />
              <Route path="/groups/:id" element={<GroupCard />} />
              <Route path="/geo" element={<CountriesScreen />} />
              <Route path="/geo/changelog" element={<CountryChangeLog />} />
              <Route path="/geo/growth" element={<CountryGrowth />} />
              <Route path="/geo/prefixes" element={<DialPrefixes />} />
              <Route path="/geo/sanctions" element={<GeoSanctions />} />
              <Route path="/geo/:code" element={<CountryCard />} />
              <Route path="/staff/*" element={<StaffScreen />} />
              <Route path="/security/dashboard" element={<SecurityDashboard />} />
              <Route path="/security/*" element={<SecurityScreen />} />
              {/* Navigatsiyada yo'q — komponentlarni tekshirish uchun */}
              <Route path="/dev/danger" element={<DangerDemoScreen />} />
              <Route path="/dev/auth" element={<AuthDemoScreen />} />
              {/* A12/A13/A25/A26/A27 modul-ekranlari — registratsiya 2026-07-23 */}
              <Route path="/seo/robots" element={<RobotsEditorScreen />} />
              <Route path="/seo/redirects" element={<RedirectsScreen />} />
              <Route path="/seo/not-found" element={<NotFoundMonitorScreen />} />
              <Route path="/developers/apps" element={<AppsScreen />} />
              <Route path="/developers/bot-tokens" element={<BotTokensScreen />} />
              <Route path="/developers/leaked" element={<LeakedTokensScreen />} />
              <Route path="/developers/webhooks" element={<WebhooksScreen />} />
              <Route path="/developers/oauth" element={<OAuthAppsScreen />} />
              <Route path="/developers/rate-limits" element={<RateLimitsScreen />} />
              <Route path="/antifraud/events" element={<SuspiciousQueueScreen />} />
              <Route path="/antifraud/rules" element={<RulesScreen />} />
              <Route path="/antifraud/blacklist" element={<BlacklistsScreen />} />
              <Route path="/antifraud/devices" element={<DevicesScreen />} />
              <Route path="/antifraud/payments" element={<PaymentFraudScreen />} />
              <Route path="/antifraud/referral-abuse" element={<ReferralAbuseScreen />} />
              <Route path="/automation/workflows" element={<WorkflowsScreen />} />
              <Route path="/automation/executions" element={<ExecutionsScreen />} />
              <Route path="/automation/scheduled" element={<ScheduledTasksScreen />} />
              <Route path="/automation/triggers" element={<TriggersScreen />} />
              <Route path="/automation/webhooks" element={<OutgoingWebhooksScreen />} />
              <Route path="/automation/errors" element={<ErrorsScreen />} />
              <Route path="/devtools/console" element={<ApiConsoleScreen />} />
              <Route path="/devtools/cache" element={<CacheScreen />} />
              <Route path="/devtools/health" element={<HealthScreen />} />
              <Route path="/devtools/test-users" element={<TestUsersScreen />} />
              <Route path="/devtools/migrations" element={<MigrationsScreen />} />
              <Route path="/devtools/restart" element={<RestartScreen />} />
              {/* --- BATCH-1 (2026-07-26): A1/A9/A14/A19/A22/A26 --- */}
              <Route path="/auth/hardware-2fa" element={<HardwareTwoFAScreen />} />
              <Route path="/auth/onboarding" element={<OnboardingScreen />} />
              <Route path="/localization/versions" element={<LocalizationVersions />} />
              <Route path="/localization/community" element={<LocalizationCommunity />} />
              <Route path="/localization/translators" element={<LocalizationTranslators />} />
              <Route path="/localization/screenshots" element={<LocalizationScreenshots />} />
              <Route path="/flags/experiment-results" element={<AbTestResults />} />
              <Route path="/flags/rollout-status" element={<RolloutStatus />} />
              <Route path="/legal/basis" element={<LegalBasisScreen />} />
              <Route path="/legal/jurisdiction" element={<LegalJurisdictionScreen />} />
              <Route path="/legal/blocking" element={<LegalBlockingScreen />} />
              <Route path="/legal/collection" element={<LegalDataCollectionScreen />} />
              <Route path="/legal/response-prep" element={<LegalResponsePrepScreen />} />
              <Route path="/legal/send-confirm" element={<LegalSendConfirmScreen />} />
              <Route path="/legal/data-export" element={<LegalDataExportScreen />} />
              <Route path="/legal/erasure" element={<LegalErasureScreen />} />
              <Route path="/verification/review" element={<VerificationReview />} />
              <Route path="/verification/manage" element={<VerificationManage />} />
              <Route path="/verification/verifiers" element={<ThirdPartyVerifiers />} />
              <Route path="/verification/verifiers/audit" element={<VerifierAudit />} />
              <Route path="/automation/builder" element={<WorkflowBuilderScreen />} />
              <Route path="/automation/conditions" element={<ConditionsActionsScreen />} />
              <Route path="/automation/test" element={<TestModeScreen />} />
              <Route path="/automation/integrations" element={<IntegrationsScreen />} />
              <Route path="/automation/scripts" element={<ScriptsScreen />} />
              <Route path="/automation/audit" element={<AutomationAuditScreen />} />
              {/* --- BATCH-2 (2026-07-26): A4/A5/A15/A20/A23/A25 --- */}
              <Route path="/broadcasts/audience" element={<AudienceSelect />} />
              <Route path="/broadcasts/segments" element={<SegmentBuilder />} />
              <Route path="/broadcasts/editor" element={<MessageEditor />} />
              <Route path="/broadcasts/ab" element={<AbVariants />} />
              <Route path="/broadcasts/approvals" element={<ApprovalFlow />} />
              <Route path="/broadcasts/delivery" element={<DeliveryStats />} />
              <Route path="/broadcasts/engagement" element={<OpenClickStats />} />
              <Route path="/broadcasts/unsubscribes" element={<Unsubscribes />} />
              <Route path="/broadcasts/token-health" element={<PushTokenHealth />} />
              <Route path="/antifraud/risk" element={<RiskDashboardScreen />} />
              <Route path="/antifraud/rules/new" element={<RuleCreateScreen />} />
              <Route path="/antifraud/backtest" element={<BacktestScreen />} />
              <Route path="/antifraud/models" element={<MlModelsScreen />} />
              <Route path="/antifraud/scoring" element={<ScoringConfigScreen />} />
              <Route path="/antifraud/auto-actions" element={<AutoActionsScreen />} />
              <Route path="/antifraud/linked" element={<LinkedAccountsScreen />} />
              <Route path="/antifraud/synthetic" element={<SyntheticActivityScreen />} />
              <Route path="/antifraud/stars" element={<StarsFraudScreen />} />
              <Route path="/antifraud/reports" element={<FraudReportsScreen />} />
              <Route path="/bots/rate-limits" element={<RateLimitSettingsScreen />} />
              <Route path="/bots/restrict" element={<RestrictBotScreen />} />
              <Route path="/bots/delete" element={<DeleteBotScreen />} />
              <Route path="/bots/vip-limits" element={<VipLimitScreen />} />
              <Route path="/mini-apps/queue" element={<AppModerationQueue />} />
              <Route path="/mini-apps/url-security" element={<AppUrlSecurity />} />
              <Route path="/mini-apps/catalog" element={<AppCatalogScreen />} />
              <Route path="/mini-apps/categories" element={<AppCategoriesScreen />} />
              <Route path="/mini-apps/reviews" element={<AppReviewsScreen />} />
              <Route path="/mini-apps/suspend" element={<AppSuspendScreen />} />
              <Route path="/mini-apps/delete" element={<AppDeleteScreen />} />
              <Route path="/ads/advertisers/:id" element={<AdvertiserCardScreen />} />
              <Route path="/ads/advertiser-card" element={<AdvertiserCardScreen />} />
              <Route path="/ads/balance" element={<AdvertiserBalanceScreen />} />
              <Route path="/ads/targeting" element={<TargetingScreen />} />
              <Route path="/ads/placements" element={<PlacementsScreen />} />
              <Route path="/ads/placement-remove" element={<PlacementRemoveScreen />} />
              <Route path="/ads/placements-overview" element={<AdPlacementsOverviewScreen />} />
              <Route path="/ads/pricing" element={<PricingCpmScreen />} />
              <Route path="/ads/auction" element={<AuctionSettingsScreen />} />
              <Route path="/ads/fraud" element={<FraudAdsScreen />} />
              <Route path="/ads/brand-safety" element={<BrandSafetyScreen />} />
              <Route path="/ads/reports" element={<AdsReportsScreen />} />
              <Route path="/group-ops/search" element={<GroupAdvancedSearch />} />
              <Route path="/group-ops/bulk" element={<GroupBulkActions />} />
              <Route path="/group-ops/linked" element={<GroupLinkedChats />} />
              <Route path="/group-ops/pinned" element={<GroupPinnedMessages />} />
              <Route path="/group-ops/ads" element={<GroupAdPosts />} />
              <Route path="/group-ops/monetization" element={<GroupMonetization />} />
              <Route path="/group-ops/warn" element={<GroupSendWarning />} />
              <Route path="/group-ops/restrict" element={<GroupRestrict />} />
              <Route path="/group-ops/purge" element={<GroupPurgePosts />} />
              <Route path="/group-ops/hide" element={<GroupHideFromSearch />} />
              <Route path="/group-ops/archive" element={<GroupArchive />} />
              <Route path="/group-ops/delete" element={<GroupDelete />} />
              <Route path="/staff/role-editor" element={<RoleEditorScreen />} />
              <Route path="/staff/access-requests" element={<AccessRequestsScreen />} />
              <Route path="/staff/jit" element={<StaffJITScreen />} />
              <Route path="/staff/assignment-rules" element={<AssignmentRulesScreen />} />
              <Route path="/staff/shifts" element={<ShiftsScreen />} />
              <Route path="/staff/workload" element={<WorkloadScreen />} />
              <Route path="/staff/kpi" element={<PerformanceKpiScreen />} />
              <Route path="/staff/onboarding" element={<StaffOnboardingScreen />} />
              <Route path="/staff/offboarding" element={<OffboardingScreen />} />
              <Route path="/staff/activity" element={<StaffActivityAuditScreen />} />
              <Route path="/staff/announcements" element={<StaffAnnouncementsScreen />} />
              <Route path="/staff/training" element={<TrainingScreen />} />
              <Route path="/staff/settings" element={<StaffSettingsScreen />} />
              {/* --- BATCH-3 (2026-07-26): A8/A11/A13/A16/A17/A18 --- */}
              <Route path="/strikes/manual" element={<ManualRestrictionScreen />} />
              <Route path="/strikes/revoke" element={<RevokeRestrictionScreen />} />
              <Route path="/strikes/duration" element={<ChangeDurationScreen />} />
              <Route path="/strikes/auto-rules" element={<AutoRulesScreen />} />
              <Route path="/strikes/spam-waves" element={<SpamWaveScreen />} />
              <Route path="/strikes/templates" element={<RestrictionTemplatesScreen />} />
              <Route path="/strikes/amnesty" element={<MassAmnestyScreen />} />
              <Route path="/strikes/wrongful-bans" element={<WrongfulBanAnalysisScreen />} />
              <Route path="/strikes/reasons" element={<BanReasonsCatalogScreen />} />
              <Route path="/strikes/fairness" element={<FairnessReportScreen />} />
              <Route path="/appeals/decisions" element={<AppealDecisionsScreen />} />
              <Route path="/blocks/devices" element={<DeviceBlocksScreen />} />
              <Route path="/blocks/phones" element={<PhoneBlocksScreen />} />
              <Route path="/blocks/reregistration" element={<ReregistrationPreventionScreen />} />
              <Route path="/developers/apps/:id" element={<AppCardScreen />} />
              <Route path="/developers/app-card" element={<AppCardScreen />} />
              <Route path="/developers/usage" element={<UsageStatsScreen />} />
              <Route path="/developers/app-block" element={<AppBlockScreen />} />
              <Route path="/developers/abuse" element={<AbuseDetectionScreen />} />
              <Route path="/developers/forced-revoke" element={<ForcedRevokeScreen />} />
              <Route path="/developers/vip-limits" element={<VipLimitsScreen />} />
              <Route path="/developers/rotation-log" element={<RotationLogScreen />} />
              <Route path="/developers/service-keys" element={<ServiceKeysScreen />} />
              <Route path="/developers/kms" element={<KmsKeysScreen />} />
              <Route path="/developers/key-export" element={<KeyExportScreen />} />
              <Route path="/developers/certs" element={<CertsScreen />} />
              <Route path="/developers/provider-keys" element={<ProviderKeysScreen />} />
              <Route path="/developers/key-policy" element={<KeyPolicyScreen />} />
              <Route path="/developers/key-audit" element={<KeyAuditScreen />} />
              <Route path="/security/incidents" element={<SecurityIncidentsScreen />} />
              <Route path="/security/fraud-rules" element={<FraudRulesScreen />} />
              <Route path="/security/fraud-cases" element={<FraudCasesScreen />} />
              <Route path="/security/anomaly" element={<AnomalyScreen />} />
              <Route path="/security/bot-farms" element={<BotFarmsScreen />} />
              <Route path="/security/mass-registration" element={<MassRegistrationScreen />} />
              <Route path="/security/credential-stuffing" element={<CredentialStuffingScreen />} />
              <Route path="/security/ddos" element={<DdosScreen />} />
              <Route path="/security/waf" element={<WafRulesScreen />} />
              <Route path="/security/ip-lists" element={<IpListsScreen />} />
              <Route path="/security/vuln-scans" element={<VulnScansScreen />} />
              <Route path="/security/dependency-audit" element={<DependencyAuditScreen />} />
              <Route path="/security/pentest" element={<PentestScreen />} />
              <Route path="/security/policies" element={<SecurityPoliciesScreen />} />
              <Route path="/geo/map" element={<CountriesMapScreen />} />
              <Route path="/geo/rollout" element={<StagedRolloutScreen />} />
              <Route path="/geo/feature-restrictions" element={<FeatureRestrictionsScreen />} />
              <Route path="/geo/payment-restrictions" element={<PaymentRestrictionsScreen />} />
              <Route path="/geo/content-restrictions" element={<ContentRestrictionsScreen />} />
              <Route path="/geo/locale" element={<LocaleSettingsScreen />} />
              <Route path="/geo/operators" element={<PhoneOperatorsScreen />} />
              <Route path="/geo/blocked-ranges" element={<BlockedRangesScreen />} />
              <Route path="/geo/ip-geo" element={<IpGeoBaseScreen />} />
              <Route path="/geo/vpn" element={<VpnProxyScreen />} />
              <Route path="/geo/tor" element={<TorNodesScreen />} />
              <Route path="/geo/dc-routing" element={<DcRoutingScreen />} />
              <Route path="/geo/residency" element={<DataResidencyScreen />} />
              <Route path="/geo/legal-registry" element={<LegalRegistryScreen />} />
              <Route path="/geo/anomalies" element={<GeoAnomaliesScreen />} />
              <Route path="/geo/premium-pricing" element={<PremiumPricingScreen />} />
              <Route path="/analytics/screen-time" element={<ScreenTime />} />
              <Route path="/analytics/funnel-builder" element={<FunnelBuilder />} />
              <Route path="/analytics/ltv" element={<Ltv />} />
              <Route path="/analytics/conversion-funnel" element={<ConversionFunnel />} />
              <Route path="/analytics/viral" element={<ViralCoefficient />} />
              <Route path="/analytics/premium-conversion" element={<PremiumConversion />} />
              <Route path="/analytics/bot-ecosystem" element={<BotEcosystemOverview />} />
              <Route path="/analytics/scheduled-reports" element={<ScheduledReports />} />
              <Route path="/analytics/export-center" element={<ExportCenter />} />
              <Route path="/analytics/data-dictionary" element={<DataDictionary />} />
              <Route path="/infra/error-logs" element={<InfraErrorLogsScreen />} />
              <Route path="/infra/db" element={<InfraDbScreen />} />
              <Route path="/infra/migrations" element={<InfraMigrationsScreen />} />
              {/* A17.02-22 — real single-node infra (host/DC/servers/slow-q/api-metrics/cdn/backups/deploys) */}
              <Route path="/infra/servers" element={<InfraServersScreen />} /> {/* A17.02 */}
              <Route path="/infra/datacenters" element={<InfraDataCentersScreen />} /> {/* A17.03 */}
              <Route path="/infra/dc-load" element={<InfraDcLoadScreen />} /> {/* A17.04 */}
              <Route path="/infra/dc-assignment" element={<InfraDcAssignmentScreen />} /> {/* A17.05 */}
              <Route path="/infra/migration-control" element={<InfraMigrationControlScreen />} /> {/* A17.06 */}
              <Route path="/infra/queues" element={<InfraQueuesScreen />} /> {/* A17.07 */}
              <Route path="/infra/slow-queries" element={<InfraSlowQueriesScreen />} /> {/* A17.11 */}
              <Route path="/infra/api-latency" element={<InfraApiLatencyScreen />} /> {/* A17.12 */}
              <Route path="/infra/cdn" element={<InfraCdnScreen />} /> {/* A17.17 */}
              <Route path="/infra/backups" element={<InfraBackupsScreen />} /> {/* A17.19 */}
              <Route path="/infra/restore-drill" element={<InfraRestoreDrillScreen />} /> {/* A17.20 */}
              <Route path="/infra/deploys" element={<InfraDeployHistoryScreen />} /> {/* A17.21 */}
              <Route path="/infra/rollback" element={<InfraRollbackScreen />} /> {/* A17.22 */}
              <Route path="/infra/planned/:id" element={<InfraPlanned />} />
              {/* --- BATCH-4 (2026-07-26): A3/A6/A7/A12/A21/A24 --- */}
              <Route path="/settings/branding" element={<BrandingSettings />} />
              <Route path="/settings/domains" element={<DomainsSettings />} />
              <Route path="/settings/smtp" element={<SmtpSettings />} />
              <Route path="/settings/push-certs" element={<PushCertificates />} />
              <Route path="/settings/file-storage" element={<FileStorageSettings />} />
              <Route path="/settings/cdn" element={<CdnSettings />} />
              <Route path="/settings/search-index" element={<SearchIndexSettings />} />
              <Route path="/settings/cache" element={<CacheManagement />} />
              <Route path="/settings/registration" element={<RegistrationSettings />} />
              <Route path="/settings/media-limits" element={<MediaLimits />} />
              <Route path="/settings/message-limits" element={<MessageLimits />} />
              <Route path="/settings/group-limits" element={<GroupLimits />} />
              <Route path="/settings/bot-limits" element={<BotLimits />} />
              <Route path="/settings/premium-packages" element={<PremiumPackages />} />
              <Route path="/settings/pricing" element={<Pricing />} />
              <Route path="/complaints" element={<ComplaintsList />} />
              <Route path="/complaints/unfounded" element={<UnfoundedComplaints />} />
              <Route path="/complaints/:id" element={<ComplaintCard />} />
              <Route path="/support/history" element={<CorrespondenceHistory />} />
              <Route path="/support/assign" element={<AssignmentQueue />} />
              <Route path="/support/priority" element={<PriorityBoard />} />
              <Route path="/support/tags" element={<TagsCategories />} />
              <Route path="/support/merge" element={<MergeDuplicates />} />
              <Route path="/support/macros" element={<MacrosScreen />} />
              <Route path="/support/notes" element={<InternalDiscussion />} />
              <Route path="/support/kb" element={<KnowledgeBase />} />
              <Route path="/support/auto-replies" element={<AutoReplies />} />
              <Route path="/support/csat" element={<CsatScreen />} />
              <Route path="/support/agents" element={<AgentStats />} />
              <Route path="/support/workload" element={<TeamWorkload />} />
              <Route path="/support/reports" element={<TicketReports />} />
              <Route path="/users/search" element={<AdvancedUserSearch />} />
              <Route path="/users/segments" element={<SavedSegments />} />
              <Route path="/users/bulk" element={<BulkUserActions />} />
              <Route path="/users/export" element={<UserExport />} />
              <Route path="/users/import" element={<UserImport />} />
              <Route path="/users/pii-log" element={<PiiAccessLog />} />
              <Route path="/users/:id/profile-history" element={<ProfileHistory />} />
              <Route path="/users/:id/data-export" element={<UserDataExport />} />
              <Route path="/users/:id/messages" element={<UserMessageView />} />
              <Route path="/users/:id/restrict" element={<UserRestrict />} />
              <Route path="/users/:id/shadowban" element={<UserShadowban />} />
              <Route path="/users/:id/username" element={<UsernameChange />} />
              <Route path="/users/:id/scrub" element={<AvatarScrub />} />
              <Route path="/users/:id/delete" element={<AccountDelete />} />
              <Route path="/users/:id/restore" element={<AccountRestore />} />
              <Route path="/users/:id/reward" element={<UserReward />} />
              <Route path="/users/:id/impersonate" element={<Impersonation />} />
              <Route path="/catalog/sticker-moderation" element={<StickerModerationScreen />} />
              <Route path="/catalog/trending-stickers" element={<TrendingStickersScreen />} />
              <Route path="/catalog/copyright" element={<CopyrightComplaintsScreen />} />
              <Route path="/catalog/sticker-authors" element={<StickerAuthorsScreen />} />
              <Route path="/catalog/tgs-validation" element={<TgsValidationScreen />} />
              <Route path="/catalog/emoji-sets" element={<CustomEmojiSetsScreen />} />
              <Route path="/catalog/emoji-statuses" element={<EmojiStatusesScreen />} />
              <Route path="/catalog/reactions" element={<ReactionsManagementScreen />} />
              <Route path="/catalog/emoji-moderation" element={<EmojiModerationScreen />} />
              <Route path="/catalog/featured" element={<FeaturedCatalogScreen />} />
              <Route path="/catalog/mini-apps" element={<MiniAppCatalogScreen />} />
              <Route path="/catalog/categories" element={<CatalogCategoriesScreen />} />
              <Route path="/catalog/recommendation" element={<RecommendationSettingsScreen />} />
              <Route path="/catalog/search-ranking" element={<SearchRankingScreen />} />
              <Route path="/catalog/media-stats" element={<MediaStorageStatsScreen />} />
              <Route path="/catalog/reports" element={<CatalogReportsScreen />} />
              <Route path="/catalog/duplicate-media" element={<DuplicateMediaScreen />} />
              <Route path="/seo/dashboard" element={<SeoDashboardScreen />} />
              <Route path="/seo/meta" element={<MetaTemplatesScreen />} />
              <Route path="/seo/opengraph" element={<OpenGraphScreen />} />
              <Route path="/seo/canonical" element={<CanonicalScreen />} />
              <Route path="/seo/sitemap" element={<SitemapScreen />} />
              <Route path="/seo/index-policy" element={<IndexPolicyScreen />} />
              <Route path="/seo/noindex" element={<NoindexScreen />} />
              <Route path="/seo/schema" element={<SchemaOrgScreen />} />
              <Route path="/seo/hreflang" element={<HreflangScreen />} />
              <Route path="/seo/keywords" element={<KeywordsScreen />} />
              <Route path="/seo/competitors" element={<CompetitorsScreen />} />
              <Route path="/seo/content-gaps" element={<ContentGapsScreen />} />
              <Route path="/seo/internal-links" element={<InternalLinksScreen />} />
              <Route path="/seo/web-vitals" element={<WebVitalsScreen />} />
              <Route path="/seo/page-speed" element={<PageSpeedScreen />} />
              <Route path="/seo/mobile" element={<MobileFriendlyScreen />} />
              <Route path="/seo/search-console" element={<SearchConsoleScreen />} />
              <Route path="/seo/yandex" element={<YandexWebmasterScreen />} />
              <Route path="/seo/landing" element={<LandingPagesScreen />} />
              <Route path="/seo/blog" element={<BlogCmsScreen />} />
              <Route path="/seo/miniapp" element={<MiniAppSeoScreen />} />
              <Route path="/seo/deeplink" element={<DeepLinkPreviewScreen />} />
              <Route path="/seo/audit" element={<SeoAuditScreen />} />
              <Route path="/moderation/bots" element={<BotModerationScreen />} />
              <Route path="/moderation/stickers" element={<StickerEmojiModerationScreen />} />
              <Route path="/moderation/usernames" element={<UsernameModerationScreen />} />
              <Route path="/moderation/avatars" element={<AvatarModerationScreen />} />
              <Route path="/moderation/review-queue" element={<SecondReviewQueueScreen />} />
              <Route path="/moderation/context" element={<ContextViewScreen />} />
              <Route path="/moderation/user-history" element={<UserHistoryPanelScreen />} />
              <Route path="/moderation/similar" element={<SimilarCasesScreen />} />
              <Route path="/moderation/decision" element={<DecisionFormScreen />} />
              <Route path="/moderation/second-opinion" element={<SecondOpinionScreen />} />
              <Route path="/moderation/ml-models" element={<ModMlModelsScreen />} />
              <Route path="/moderation/model-quality" element={<ModelQualityScreen />} />
              <Route path="/moderation/thresholds" element={<ThresholdTuningScreen />} />
              <Route path="/moderation/hashes" element={<HashDatabaseScreen />} />
              <Route path="/moderation/phash" element={<PerceptualHashScreen />} />
              <Route path="/moderation/keywords" element={<KeywordListsScreen />} />
              <Route path="/moderation/file-hashes" element={<FileHashBlacklistScreen />} />
              <Route path="/moderation/whitelists" element={<WhitelistsScreen />} />
              <Route path="/moderation/rules" element={<RuleBuilderScreen />} />
              <Route path="/moderation/rule-test" element={<RuleTestScreen />} />
              <Route path="/moderation/audit" element={<DecisionAuditScreen />} />
              <Route path="/moderation/errors" element={<ErrorDecisionsScreen />} />
              <Route path="/moderation/training" element={<TrainingModeScreen />} />
              <Route path="/moderation/policies" element={<ModerationPolicyScreen />} />
              <Route path="/moderation/transparency" element={<TransparencyReportScreen />} />
              <Route path="/moderation/inter-rater" element={<InterRaterScreen />} />
              {/* --- BATCH-5 (2026-07-26): A27 dev-tools --- */}
              <Route path="/devtools/requests" element={<RequestInspectorScreen />} />
              <Route path="/devtools/webhook-debug" element={<WebhookDebugScreen />} />
              <Route path="/devtools/updates" element={<UpdateViewerScreen />} />
              <Route path="/devtools/events" element={<EventStreamScreen />} />
              <Route path="/devtools/trace" element={<ErrorTraceScreen />} />
              <Route path="/devtools/sandbox" element={<SandboxScreen />} />
              <Route path="/devtools/generate" element={<DataGeneratorScreen />} />
              <Route path="/devtools/config-diff" element={<ConfigDiffScreen />} />
              <Route path="/index.html" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppShell>
        }
      />
    </Routes>
  )
}

export function App() {
  return (
    // panel endi ROOT `/` da (asosiy) — avval `/v2` ostida edi.
    <BrowserRouter basename="/">
      <I18nProvider>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <AuthGate />
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </I18nProvider>
    </BrowserRouter>
  )
}
